/**
 * Cart upsell discount codes — apply only when added from cart upsell, not from PDP.
 * Preserves other active discount codes (e.g. WELKOM10).
 * Removes those codes when the last upsell line is deleted from the cart.
 */
(function () {
  const STORAGE_KEY = "oo_cart_upsell_applied_codes";
  let pendingApplyCode = null;
  let pendingRemoveCode = null;
  let submitListenerInstalled = false;

  function normalizeCode(code) {
    return (code || "").trim();
  }

  function parseDiscountCodes(value) {
    return String(value || "")
      .split(",")
      .map((code) => normalizeCode(code))
      .filter(Boolean);
  }

  function codesMatch(a, b) {
    return normalizeCode(a).toUpperCase() === normalizeCode(b).toUpperCase();
  }

  function readAppliedCodes() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.map(normalizeCode).filter(Boolean)
        : [];
    } catch (e) {
      return [];
    }
  }

  function writeAppliedCodes(codes) {
    const unique = [];
    const seen = new Set();
    codes.forEach((code) => {
      const normalized = normalizeCode(code);
      const key = normalized.toUpperCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      unique.push(normalized);
    });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
  }

  function rememberAppliedCode(code) {
    writeAppliedCodes([...readAppliedCodes(), code]);
  }

  function forgetAppliedCodes(codesToForget) {
    const forget = new Set(
      (Array.isArray(codesToForget) ? codesToForget : [codesToForget]).map(
        (code) => normalizeCode(code).toUpperCase(),
      ),
    );
    writeAppliedCodes(
      readAppliedCodes().filter(
        (code) => !forget.has(normalizeCode(code).toUpperCase()),
      ),
    );
  }

  async function fetchCart() {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart.js`, { credentials: "same-origin" });
    if (!response.ok) throw new Error(response.status);
    return response.json();
  }

  function extractCartDiscountCodes(cart) {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .map((entry) => {
          if (typeof entry === "string") return normalizeCode(entry);
          return normalizeCode(entry.code || entry.title);
        })
        .filter(Boolean);
    }

    return (cart.cart_level_discount_applications || [])
      .map((app) => normalizeCode(app.title))
      .filter(Boolean);
  }

  function isUpsellLine(item) {
    const properties = item.properties || {};
    return String(properties._cart_upsell || "") === "1";
  }

  function cartHasUpsellItem(cart) {
    return (cart.items || []).some(isUpsellLine);
  }

  function cartHasAnchorItem(cart) {
    return (cart.items || []).some((item) => !isUpsellLine(item));
  }

  function upsellCodesFromCart(cart) {
    const codes = [];
    (cart.items || []).forEach((item) => {
      if (!isUpsellLine(item)) return;
      (item.discounts || []).forEach((discount) => {
        const code = normalizeCode(discount.title || discount.code);
        if (code) codes.push(code);
      });
    });
    return codes;
  }

  async function setCartDiscountCodes(codes) {
    const unique = [];
    const seen = new Set();

    codes.forEach((code) => {
      const normalized = normalizeCode(code);
      const key = normalized.toUpperCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      unique.push(normalized);
    });

    const root = window.Shopify?.routes?.root ?? "/";
    await fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: unique.join(",") }),
    });
  }

  async function addDiscountCode(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return false;

    const cart = await fetchCart();
    const current = extractCartDiscountCodes(cart);
    rememberAppliedCode(normalized);
    if (current.some((existing) => codesMatch(existing, normalized))) {
      return false;
    }

    await setCartDiscountCodes([...current, normalized]);
    return true;
  }

  async function removeDiscountCodes(codesToRemove) {
    const removeSet = new Set(
      (Array.isArray(codesToRemove) ? codesToRemove : [codesToRemove])
        .map((code) => normalizeCode(code).toUpperCase())
        .filter(Boolean),
    );
    if (!removeSet.size) return false;

    const cart = await fetchCart();
    const current = extractCartDiscountCodes(cart);
    const remaining = current.filter(
      (code) => !removeSet.has(normalizeCode(code).toUpperCase()),
    );

    forgetAppliedCodes([...removeSet]);
    if (remaining.length === current.length) return false;

    await setCartDiscountCodes(remaining);
    return true;
  }

  async function syncUpsellCodesAfterCartChange() {
    const cart = await fetchCart();
    const stored = readAppliedCodes();
    const fromLines = upsellCodesFromCart(cart);
    const codes = [...stored, ...fromLines];
    if (!codes.length) return false;

    // Discount is only valid while a regular (non-upsell) product remains.
    if (!cartHasAnchorItem(cart) || !cartHasUpsellItem(cart)) {
      return removeDiscountCodes(codes);
    }

    const inapplicable = (cart.discount_codes || [])
      .filter((entry) => entry && entry.applicable === false)
      .map((entry) => normalizeCode(entry.code || entry.title))
      .filter((code) =>
        codes.some((known) => codesMatch(known, code)),
      );

    if (!inapplicable.length) return false;
    return removeDiscountCodes(inapplicable);
  }

  async function fetchCartSections(cart) {
    if (!cart?.getSectionsToRender) return null;

    const root = window.Shopify?.routes?.root ?? "/";
    const sectionIds = cart
      .getSectionsToRender()
      .map((section) => section.id)
      .join(",");

    const response = await fetch(`${root}?sections=${sectionIds}`);
    if (!response.ok) return null;

    return { sections: await response.json() };
  }

  async function refreshCartDrawer() {
    const cart = document.querySelector("cart-drawer");
    if (!cart) return;

    if (typeof cart.refreshCartDrawer === "function") {
      cart.refreshCartDrawer({});
      return;
    }

    if (typeof cart.renderContents === "function") {
      const refreshed = await fetchCartSections(cart);
      if (refreshed) cart.renderContents(refreshed);
    }
  }

  function installSubmitListener() {
    if (submitListenerInstalled) return;
    submitListenerInstalled = true;

    document.addEventListener(
      "submit",
      (event) => {
        const upsellForm = event.target.closest?.(
          ".oo-cart-upsell-item__product-form",
        );
        if (upsellForm) {
          const productForm = upsellForm.closest("product-form");
          pendingApplyCode = normalizeCode(
            productForm?.dataset.ooUpsellDiscountCode,
          );
          pendingRemoveCode = null;
          return;
        }

        const form = event.target.closest?.('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const productForm = form.closest("product-form");
        if (!productForm) return;

        if (productForm.dataset.ooAutoDiscountCodes) {
          pendingApplyCode = null;
          pendingRemoveCode = null;
          return;
        }

        const upsellCodes = parseDiscountCodes(
          productForm.dataset.ooUpsellDiscountCode,
        );
        if (upsellCodes.length) {
          pendingApplyCode = null;
          pendingRemoveCode = upsellCodes;
        }
      },
      true,
    );
  }

  function installCartUpdateHook() {
    if (window.__ooCartUpsellDiscountHook) return;
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      document.addEventListener("DOMContentLoaded", installCartUpdateHook, {
        once: true,
      });
      return;
    }

    window.__ooCartUpsellDiscountHook = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const applyCode = pendingApplyCode;
      const removeCode = pendingRemoveCode;
      pendingApplyCode = null;
      pendingRemoveCode = null;

      (async () => {
        try {
          let changed = false;

          if (event?.source === "product-form") {
            if (applyCode) {
              changed = await addDiscountCode(applyCode);
            } else if (removeCode) {
              changed = await removeDiscountCodes(removeCode);
            }
            changed = (await syncUpsellCodesAfterCartChange()) || changed;
          } else if (event?.source === "cart-items") {
            changed = await syncUpsellCodesAfterCartChange();
          }

          if (changed) {
            await refreshCartDrawer();
          }
        } catch (e) {
          console.warn("[oo-cart-upsell-discount]", e);
        }
      })();
    });
  }

  installSubmitListener();
  installCartUpdateHook();
})();
